import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Divider,
  HStack,
  IconButton,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

import { CompanyDocumentType, IdentityVerificationStatus } from '@blockpulse3/data/shared';
import {
  IdentityType,
  IndividualDocumentType,
  SubscriptionInfosFragment,
  UserRole,
  useDeleteCompanyDocumentMutation,
  useDeleteKycDocumentMutation,
  useGetDocumentPdfUrlLazyQuery,
  useGetSubscriptionIdentityDocumentsQuery,
  useGetSubscriptionQuery,
  useHandleIdentityApprovalMutation,
} from '@blockpulse3/graphql/hooks';
import { getDocumentIds } from '@blockpulse3/helpers';
import {
  AddIdentityDocuments,
  DeleteConfirmModal,
  DocumentLink,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
  WarningCard,
  useGetKybDocuments,
  useGetKycDocuments,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { UltimateBeneficialOwnerCard } from './UltimateBeneficialOwnerCard';

type IDocumentTypeMessage = keyof IntlMessages['SubscriptionDocumentsList'];

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
} & Omit<ResponsiveModalProps, 'children'>;

export function IdentityValidationModal({ subscriptionId, onClose, ...props }: Props): JSX.Element {
  const i18nSubscriptionDocumentsList = useTranslations('SubscriptionDocumentsList');
  const i18nIdentityValidationModal = useTranslations('IdentityValidationModal');
  const i18nGeneral = useTranslations('General');

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isAddingDocuments, setIsAddingDocuments] = useState<boolean>(false);

  const confirmRef = useRef(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<
    CompanyDocumentType | IndividualDocumentType | undefined
  >(undefined);

  const { isOpen, onOpen, onClose: onCloseConfirmationModal } = useDisclosure();

  const { user } = useUser();

  const {
    data: subscriptionData,
    refetch: refetchSubscription,
    loading: subscriptionLoading,
  } = useGetSubscriptionQuery({
    variables: {
      subscriptionId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: subscriptionDocumentsData,
    refetch: refetchDocuments,
    loading: documentLoading,
  } = useGetSubscriptionIdentityDocumentsQuery({
    variables: {
      subscriptionId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const isLoading = subscriptionLoading || documentLoading;

  const [getDocumentPdfUrl] = useGetDocumentPdfUrlLazyQuery();
  const [handleIdentityApproval, { loading: isIdentityApprovalLoading }] =
    useHandleIdentityApprovalMutation();
  const [deleteCompanyDocument, { loading: isDocumentDeletionLoading }] =
    useDeleteCompanyDocumentMutation();
  const [deleteDocument, { loading: isKycDeletionLoading }] = useDeleteKycDocumentMutation();
  const [getKybDocuments] = useGetKybDocuments();
  const [getKycDocuments] = useGetKycDocuments();

  const identity = subscriptionData?.subscription.identity;
  const isCompany = identity?.type === IdentityType.COMPANY;
  const kycbVerificationStatus = isCompany
    ? identity?.companyIdentity?.kybVerificationStatus
    : identity?.individualIdentity?.kycVerificationStatus;

  const displayButton = [
    IdentityVerificationStatus.PENDING,
    IdentityVerificationStatus.REFUSED,
  ].includes(kycbVerificationStatus as IdentityVerificationStatus);
  const displayDeleteButton = [
    IdentityVerificationStatus.NONE,
    IdentityVerificationStatus.PENDING,
    IdentityVerificationStatus.REFUSED,
  ].includes(kycbVerificationStatus as IdentityVerificationStatus);

  const companyName = identity?.companyIdentity?.name || '';
  const individualIdentity = identity?.individualIdentity;
  const signerIndividualIdentity = identity?.companyIdentity?.signer?.individualIdentity;
  const individualIdentityId = individualIdentity?.id || signerIndividualIdentity?.id || '';

  const documents = subscriptionDocumentsData?.getSubscriptionIdentityDocuments || [];
  const documentIds = getDocumentIds(documents);

  const isUserAuthorized = user?.roles.some((role) =>
    [UserRole.COMPLIANCE_OFFICER, UserRole.SUPERADMIN].includes(role),
  );

  const ubos = identity?.companyIdentity?.ubos || [];

  const hasAllDocuments = isCompany
    ? Object.keys(documentIds).length >= getKybDocuments().length
    : Object.keys(documentIds).length >= getKycDocuments().length;

  useEffect(() => {
    if (!hasAllDocuments) {
      return;
    }

    setIsAddingDocuments(false);
  }, [hasAllDocuments]);

  const onDocumentClick = (documentId: string): void => {
    getDocumentPdfUrl({
      variables: {
        documentId,
      },
      fetchPolicy: 'no-cache',
      onCompleted: ({ getDocumentPdfUrl: pdfUrl }) => {
        window.open(pdfUrl, '_blank');
      },
    });
  };

  const handleIdentityApproved = (): void => {
    handleApproval(IdentityVerificationStatus.APPROVED);
  };
  const handleIdentityRefused = (): void => {
    handleApproval(IdentityVerificationStatus.REFUSED);
  };
  const handleApproval = (verificationStatus: IdentityVerificationStatus): void => {
    if (!identity) return;

    const customMessage =
      verificationStatus === IdentityVerificationStatus.REFUSED
        ? inputRef.current?.value
        : undefined;

    handleIdentityApproval({
      variables: {
        handleIdentityApprovalInput: {
          identityId: identity.id,
          verificationStatus,
          customMessage,
        },
      },
      onCompleted: () => {
        refetchSubscription();
        onCloseModal();
      },
    });
  };

  const handleFileDelete = (documentType: CompanyDocumentType | IndividualDocumentType): void => {
    // For now only handle company documents
    setSelectedDocumentType(documentType);
    onOpen();
  };

  const handleDelete = (): void => {
    if (!selectedDocumentType) return;

    const companyId = identity?.companyIdentity?.id;

    if (selectedDocumentType in CompanyDocumentType && companyId) {
      deleteCompanyDocument({
        variables: {
          deleteCompanyDocumentInput: {
            companyId,
            documentType: selectedDocumentType,
          },
        },
        onCompleted: () => {
          refetchDocuments();
          refetchSubscription();
        },
      });
    } else if (individualIdentityId) {
      deleteDocument({
        variables: {
          deleteKycDocumentInput: {
            individualIdentityId,
            documentType: selectedDocumentType,
          },
        },
        onCompleted: () => {
          refetchDocuments();
          refetchSubscription();
        },
      });
    }

    onCloseConfirmationModal();
  };

  const onCloseModal = (): void => {
    setIsAddingDocuments(false);
    onClose();
  };

  return (
    <ResponsiveModal onClose={onCloseModal} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nIdentityValidationModal('Title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Stack} spacing="4">
          <HStack justifyContent="space-between">
            <Text color="gray.600" fontWeight="600">
              {i18nIdentityValidationModal('Documents', { len: documents.length })}
            </Text>
            {isUserAuthorized && !hasAllDocuments && (
              <Button
                leftIcon={<AddIcon />}
                size="sm"
                variant="secondary"
                w="fit-content"
                onClick={(): void => setIsAddingDocuments((crr) => !crr)}
              >
                {i18nGeneral('AddDocument')}
              </Button>
            )}
          </HStack>
          {isUserAuthorized && isAddingDocuments && identity?.id && (
            <AddIdentityDocuments identityId={identity?.id} />
          )}
          {!Object.keys(documentIds).length && !isAddingDocuments && (
            <Stack layerStyle="emptyState">
              <Text>{i18nSubscriptionDocumentsList('DocumentsNotFound')}</Text>
            </Stack>
          )}
          <Stack spacing="2">
            {Object.keys(documentIds).map((documentType, index) => (
              <HStack key={index} justify="space-between">
                <DocumentLink
                  fileName={i18nSubscriptionDocumentsList(
                    documentType as IDocumentTypeMessage,
                    documentType in CompanyDocumentType
                      ? { companyName }
                      : {
                          name:
                            individualIdentity?.name ||
                            signerIndividualIdentity?.name ||
                            '[représentant légal]',
                        },
                  )}
                  onClick={(): void => onDocumentClick(documentIds[documentType])}
                />
                {displayDeleteButton && isUserAuthorized && (
                  <IconButton
                    aria-label="delete-document"
                    icon={<DeleteIcon color="red.500" />}
                    isDisabled={isDocumentDeletionLoading || isLoading || isKycDeletionLoading}
                    size="xs"
                    variant="ghost"
                    onClick={(): void =>
                      handleFileDelete(documentType as CompanyDocumentType | IndividualDocumentType)
                    }
                  />
                )}
              </HStack>
            ))}
          </Stack>
          {ubos.length && (
            <>
              <Divider />
              <Text color="gray.600" fontWeight="600">
                {i18nIdentityValidationModal('UltimateBeneficialOwners')}
              </Text>
              {ubos.map((ubo, index) => {
                return <UltimateBeneficialOwnerCard key={ubo.id} index={index} ubo={ubo} />;
              })}
            </>
          )}
          {displayButton && !isUserAuthorized && (
            <WarningCard title={i18nSubscriptionDocumentsList('WarningTitle')} />
          )}
        </ModalBody>
        {displayButton && (
          <>
            <Divider />
            <ResponsiveModalFooter>
              <Stack gap="4" w="full">
                <Accordion allowToggle variant="unstyled">
                  <AccordionItem>
                    <AccordionButton data-cy="expand-parameters">
                      <Text color="gray" fontSize="sm" textAlign="left">
                        {i18nIdentityValidationModal('SelectLabel')}
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel p="0">
                      <Textarea minH="200" ref={inputRef} size="sm" />
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
                <HStack alignSelf="end">
                  <Button
                    isDisabled={!isUserAuthorized}
                    isLoading={isIdentityApprovalLoading || isLoading}
                    variant="secondary"
                    onClick={handleIdentityRefused}
                  >
                    {i18nIdentityValidationModal('Refuse')}
                  </Button>
                  <Button
                    isDisabled={!isUserAuthorized}
                    isLoading={isIdentityApprovalLoading || isLoading}
                    onClick={handleIdentityApproved}
                  >
                    {i18nIdentityValidationModal('Approve')}
                  </Button>
                </HStack>
              </Stack>
            </ResponsiveModalFooter>
          </>
        )}
        <DeleteConfirmModal
          isOpen={isOpen}
          leastDestructiveRef={confirmRef}
          subtitle={i18nIdentityValidationModal('ConfirmModal.SubTitle')}
          title={i18nIdentityValidationModal('ConfirmModal.Title')}
          onClose={onCloseConfirmationModal}
          onDelete={handleDelete}
        />
      </ModalContent>
    </ResponsiveModal>
  );
}
